import {
  historicalConfigDeploymentAdapter,
  readConfigHomeHomeHistory,
} from '@racepointenergy/swc/scs/read-config-home-home-history'
import type {
  HistoricalConfigDeployment,
  ReadConfigHomeHomeHistoryResult,
} from '@racepointenergy/swc/scs/read-config-home-home-history'
import { useCallback, useState } from 'react'
import { Cell, Column, SortingRule } from 'react-table'
import {
  Table,
  DateCell,
  MobileSortBy,
  ConfirmButtonCell,
  DownloadButtonCell,
} from 'components/Table'
import { ConfigNameCell } from '../../components/ConfigNameCell'
import DeploymentStatus from '../components/DeploymentStatus'
import { MobileHomeDeploymentsHistory } from './MobileHomeDeploymentsHistory'
import { getSortOptions } from 'components/Table/utils'
import { HomeDeploymentsActionsCellBody } from '../../styles'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import { currentUserSelector } from 'features/auth/login/selectors'
import { selectedHomeSelector } from 'features/home/selectors'
import { selectSoftwareUpdatesInProgress } from 'features/home/components/HomeSoftware/HomeCloudSoftwareHistory/selectors'

import {
  HomeConfigActionCellProgressBar,
  HomeConfigActionCellProgressBarContainer,
  HomeConfigRowStyle,
} from '../../HomeConfigFiles/styles'
import { useHomeRouteParams } from 'features/home/hooks'
import { TableWithPaginationWrapper } from 'components/Table/styles'
//import { hasSystemAccessEnabled } from 'features/home/utils'
import { deployDomainConfigThunk } from '../../slice'
import { deployDomainConfigSelectors } from '../../selectors'
import { extractDeployErrorMessage } from '../../utils'

enum HomeConfigHistoryHeaders {
  NAME = 'name',
  DATE = 'date',
  STATE = 'state',
}

interface HomeDeploymentsHistoryTableProps {
  data: ReadConfigHomeHomeHistoryResult
}

const DEFAULT_SORT_BY = { id: HomeConfigHistoryHeaders.DATE, desc: true }

const selectAllConfigDeployments =
  historicalConfigDeploymentAdapter.getSelectors().selectAll

const ActionsCell = (cell: Cell<HistoricalConfigDeployment>) => {
  const config = cell.row.original
  const { config_id, name, configUrl } = config
  const fileName = `${name}.tar.gz`

  const dispatch = useAppDispatch()
  const [refetchHomesHomeHistory] = readConfigHomeHomeHistory.useLazyQuery()
  const user = useAppSelector(currentUserSelector)
  const home = useAppSelector(selectedHomeSelector.data)
  const redeployError = useAppSelector(deployDomainConfigSelectors.error)
  const softwareUpdatesInProgress = useAppSelector(
    selectSoftwareUpdatesInProgress
  )

  const isUpdateInProgress = softwareUpdatesInProgress?.length > 0
  const [progress, setProgress] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const { homeId } = useHomeRouteParams()

  const hasSystemAccess = false //hasSystemAccessEnabled(home)

  const handleDeploy = useCallback(async () => {
    if (home?.id && user?.id && config_id) {
      setIsLoading(true)
      try {
        await dispatch(
          deployDomainConfigThunk(
            {
              config_id: config_id,
              home_id: home.id,
              integrator_id: user.id,
            },
            setProgress
          )
        )
      } catch (e) {
        throw e
      } finally {
        setIsLoading(false)
        setProgress(0)
      }
    } else {
      throw Error('Not all the request parameters are exists')
    }
  }, [dispatch, home?.id, config_id, user.id])

  const handleRefetch = () => {
    refetchHomesHomeHistory({ homeId })
  }

  return (
    <>
      {isLoading && (
        <HomeConfigActionCellProgressBarContainer>
          <HomeConfigActionCellProgressBar progress={progress} />
        </HomeConfigActionCellProgressBarContainer>
      )}
      <HomeDeploymentsActionsCellBody isLoading={isLoading}>
        {(hasSystemAccess) && (
          <ConfirmButtonCell
            title="Deploy config file"
            body="Config was successfully deployed to the host."
            handleConfirm={handleDeploy}
            onModalClose={handleRefetch}
            isEnabledErrorModal
            errorMessage={extractDeployErrorMessage(redeployError)}
            disabled={isUpdateInProgress}
          >
            Redeploy
          </ConfirmButtonCell>
        )}
        <DownloadButtonCell url={configUrl} fileName={fileName} />
      </HomeDeploymentsActionsCellBody>
    </>
  )
}

const headers = {
  name: {
    label: 'Name',
    id: HomeConfigHistoryHeaders.NAME,
  },
  updated: {
    label: 'Deployed',
    id: HomeConfigHistoryHeaders.DATE,
  },
  status: {
    label: 'Status',
    id: HomeConfigHistoryHeaders.STATE,
  },
}

const sortOptions = getSortOptions(headers)

const columns: readonly Column<HistoricalConfigDeployment>[] = [
  {
    Header: 'Name',
    accessor: 'name',
    Cell: ConfigNameCell,
  },
  {
    Header: 'Deployed',
    accessor: 'date',
    Cell: DateCell,
  },
  {
    Header: 'Status',
    accessor: 'state',
    Cell: (cell: Cell<HistoricalConfigDeployment>) => {
      const { state } = cell.row.original

      if (!state) {
        return null
      }

      return <DeploymentStatus {...cell.row.original} />
    },
  },
  {
    Header: '',
    accessor(config) {
      return `${config.config_id}@${config.date ?? config.updated ?? 0}`
    },
    disableSortBy: true,
    id: 'actions',
    Cell: ActionsCell,
    width: 90,
  },
]

function HomeDeploymentsHistoryTable({
  data,
}: HomeDeploymentsHistoryTableProps) {
  const [sortBy, setSortBy] =
    useState<SortingRule<HistoricalConfigDeployment>>(DEFAULT_SORT_BY)

  return (
    <TableWithPaginationWrapper>
      <MobileSortBy
        headers={headers}
        sortOptions={sortOptions}
        sortBy={sortBy}
        setSortBy={setSortBy}
      />

      <Table
        columns={columns}
        data={selectAllConfigDeployments(data)}
        defaultSortBy={DEFAULT_SORT_BY}
        rowStyles={HomeConfigRowStyle}
        sortBy={sortBy}
        setSortBy={setSortBy}
        mobileViewComponent={MobileHomeDeploymentsHistory}
      />
    </TableWithPaginationWrapper>
  )
}

export default HomeDeploymentsHistoryTable
